import { ChangeEvent, useState } from 'react';
import {
  Stack,
  Text,
  useColorMode,
  useColorStyle,
  Flex,
  Switch,
  LinkButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
} from '@tonic-one/react';
import { useTranslation } from 'react-i18next';
import { storeSelector, usePolicyContext } from '@Policy/store';
import { ExchangeOnlinePolicyItem } from '@Policy/utils/types';
import { FormLabel } from '@Components/Common/FormComponent';
import { PolicyModeEnum, PolicyTypeEnum } from '@Policy/utils/constants';
import PhishingSection from '../sections/PhishingSection';
import SpamSection from '../sections/SpamSection';
import { SecurityRiskEmailBannerPreview } from '../components/EmailBannerPreview';

function SecurityRisksTab() {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  const contextStore = usePolicyContext();
  const contextSelector = storeSelector(contextStore);

  const {
    raiSecurityRisksAddBannerEnabled,
    policyType,
    policyMode,
    setRiskAiState,
  } = contextSelector((s) => {
    const template = s.template as ExchangeOnlinePolicyItem;
    return {
      raiSecurityRisksAddBannerEnabled:
        template.tmRiskAiFilters.raiSecurityRisksAddBannerEnabled,
      policyType: template.policyType,
      policyMode: template.policyMode,
      setRiskAiState: s.setRiskAiState,
    };
  });

  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);

  const isMonitor =
    policyMode === PolicyModeEnum.DefaultMonitorPolicy ||
    policyMode === PolicyModeEnum.DefaultXDRMonitorPolicy;

  return (
    <Stack gap="6x" p="4x">
      <Text color={colorStyle.color.primary}>
        {t('policy.riskAi.securityRisks.desc')}
      </Text>

      <PhishingSection />

      <SpamSection />

      {policyType === PolicyTypeEnum.Exchange && (
        <Flex alignItems="center">
          <FormLabel label={t('policy.label.emailBanner')} />
          <Switch
            value={raiSecurityRisksAddBannerEnabled}
            checked={isMonitor ? false : !!raiSecurityRisksAddBannerEnabled}
            disabled={isMonitor}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setRiskAiState({
                raiSecurityRisksAddBannerEnabled: Number(e.target.checked),
              });
            }}
          >
            {raiSecurityRisksAddBannerEnabled === 0
              ? t('policy.status.off')
              : t('policy.status.on')}
          </Switch>
          <Popover
            placement="right-start"
            isOpen={emailPreviewOpen}
            onClose={() => setEmailPreviewOpen(false)}
          >
            <PopoverTrigger>
              <LinkButton
                ml="4x"
                disabled={isMonitor}
                onClick={() => setEmailPreviewOpen((prev) => !prev)}
              >
                {t('common.label.preview')}
              </LinkButton>
            </PopoverTrigger>
            <PopoverContent padding={0}>
              <PopoverBody>
                <SecurityRiskEmailBannerPreview
                  onClose={() => setEmailPreviewOpen(false)}
                />
              </PopoverBody>
            </PopoverContent>
          </Popover>
        </Flex>
      )}
    </Stack>
  );
}

export default SecurityRisksTab;
